import QRCode from "qrcode";
import jsQR from "jsqr"; // light weight: for mining safes
import { BrowserQRCodeReader } from "@zxing/browser"; // medium weight: for mining lockers
import { DecodeHintType, BarcodeFormat } from "@zxing/library"; // Required for hint types
import { readBarcodes, type ReadResult, type ReaderOptions } from "zxing-wasm"; // heavy weight: for low contrast

type EcLevel = "L" | "M" | "Q" | "H";
// encrypted message
// H = self only: 55 characters in messasge
// Q = self only: 347 characters in message
// M = 1 recipient: 281 characters in message
// L = 5 recipients 136 characters in message

interface QrCapacity {
    version: number;
    L: number;
    M: number;
    Q: number;
    H: number;
}

const hints = new Map();
const formats = [BarcodeFormat.QR_CODE];
hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
hints.set(DecodeHintType.TRY_HARDER, true);
//hints.set(DecodeHintType.PURE_BARCODE, true); // Faster if input is clean/cropped
//hints.set(DecodeHintType.CHARACTER_SET, "UTF-8"); // Speeds up if encoding is known

export class QrCode {
    private reader = new BrowserQRCodeReader(hints);

    // Priority order for error correction levels (highest to lowest)
    private readonly EC_LEVEL_PRIORITY: EcLevel[] = ["H", "Q", "M", "L"];

    private readonly RECOVERY = {
        H: 30,
        Q: 25,
        M: 15,
        L: 7
    };

    // https://www.thonky.com/qr-code-tutorial/data-capacity-per-version
    private readonly QR_CAPACITIES: QrCapacity[] = [
        { version: 1, L: 17, M: 14, Q: 11, H: 7 },
        { version: 2, L: 32, M: 26, Q: 20, H: 14 },
        { version: 3, L: 53, M: 42, Q: 32, H: 24 },
        { version: 4, L: 78, M: 62, Q: 46, H: 34 },
        { version: 5, L: 106, M: 84, Q: 60, H: 44 },
        { version: 6, L: 134, M: 106, Q: 74, H: 58 },
        { version: 7, L: 154, M: 122, Q: 86, H: 64 },
        { version: 8, L: 192, M: 152, Q: 108, H: 84 },
        { version: 9, L: 230, M: 180, Q: 130, H: 98 },
        { version: 10, L: 271, M: 213, Q: 151, H: 119 },
        { version: 11, L: 321, M: 251, Q: 177, H: 137 },
        { version: 12, L: 367, M: 287, Q: 203, H: 155 },
        { version: 13, L: 425, M: 331, Q: 241, H: 177 },
        { version: 14, L: 458, M: 362, Q: 258, H: 194 },
        { version: 15, L: 520, M: 412, Q: 292, H: 220 },
        { version: 16, L: 586, M: 450, Q: 322, H: 250 },
        { version: 17, L: 644, M: 504, Q: 364, H: 280 },
        { version: 18, L: 718, M: 560, Q: 394, H: 310 },
        { version: 19, L: 792, M: 624, Q: 442, H: 338 },
        { version: 20, L: 858, M: 666, Q: 482, H: 382 },
        { version: 21, L: 929, M: 711, Q: 509, H: 403 },
        { version: 22, L: 1003, M: 779, Q: 565, H: 439 },
        { version: 23, L: 1091, M: 857, Q: 611, H: 461 },
        { version: 24, L: 1171, M: 911, Q: 661, H: 511 },
        { version: 25, L: 1273, M: 997, Q: 715, H: 535 },
        { version: 26, L: 1367, M: 1059, Q: 751, H: 593 },
        { version: 27, L: 1465, M: 1125, Q: 805, H: 625 },
        { version: 28, L: 1528, M: 1190, Q: 868, H: 658 },
        { version: 29, L: 1628, M: 1264, Q: 908, H: 698 },
        { version: 30, L: 1732, M: 1370, Q: 982, H: 742 },
        { version: 31, L: 1840, M: 1452, Q: 1030, H: 790 },
        { version: 32, L: 1952, M: 1538, Q: 1112, H: 842 },
        { version: 33, L: 2068, M: 1628, Q: 1168, H: 898 },
        { version: 34, L: 2188, M: 1722, Q: 1228, H: 958 },
        { version: 35, L: 2303, M: 1809, Q: 1283, H: 983 },
        { version: 36, L: 2431, M: 1911, Q: 1351, H: 1051 },
        { version: 37, L: 2563, M: 1989, Q: 1423, H: 1093 },
        { version: 38, L: 2699, M: 2099, Q: 1499, H: 1139 },
        { version: 39, L: 2809, M: 2213, Q: 1579, H: 1219 },
        { version: 40, L: 2953, M: 2331, Q: 1663, H: 1273 }
    ];

    private initialCount = 0; // byte size when message is empty

    setInitialCount(data: any) {
        this.initialCount = Buffer.byteLength(
            `B:/${encodeURIComponent(JSON.stringify(data))}/a/1`,
            "utf8"
        );
    }
    
    selectBestQrVersion(data: any): {
        version: number;
        count: number;
        limit: number;
        size: number;
        recovery: number;
        ecLevel: EcLevel;
    } | null {
        const dataByteLength = Buffer.byteLength(
            `B:/${encodeURIComponent(JSON.stringify(data))}/a/1`,
            "utf8"
        );

        for (const ecLevel of this.EC_LEVEL_PRIORITY) {
            for (const capacity of this.QR_CAPACITIES) {
                if (dataByteLength <= capacity[ecLevel]) {
                    return {
                        version: capacity.version,
                        count: dataByteLength - this.initialCount,
                        limit: capacity[ecLevel] - this.initialCount,
                        size: 4 * capacity.version + 17,
                        recovery: this.RECOVERY[ecLevel],
                        ecLevel
                    };
                }
            }
        }

        return null; // Data too big for max QR version 40 with any EC level
    }

    private selectBestQrVersion2(
        data: string,
        ecLevel: EcLevel | null = null
    ): {
        version: number;
        limit: number;
        size: number;
        recovery: number;
        ecLevel: EcLevel;
    } | null {
        // Estimate byte size of data (UTF-8 encoding)
        const dataByteLength = Buffer.byteLength(data, "utf8");
        // const dataByteLength = new TextEncoder().encode(
        //     JSON.stringify(data)
        // ).length;

        // Find the lowest version that can accommodate the data with "H" error correction
        if (ecLevel) {
            for (const capacity of this.QR_CAPACITIES) {
                if (dataByteLength <= capacity[ecLevel]) {
                    return {
                        version: capacity.version,
                        limit: capacity[ecLevel],
                        size: 4 * capacity.version + 17, // Width/height in modules (e.g., 21x21)
                        recovery: this.RECOVERY[ecLevel],
                        ecLevel
                    };
                }
            }
        } else {
            for (const ecLevel of this.EC_LEVEL_PRIORITY) {
                for (const capacity of this.QR_CAPACITIES) {
                    if (dataByteLength <= capacity[ecLevel]) {
                        return {
                            version: capacity.version,
                            limit: capacity[ecLevel],
                            size: 4 * capacity.version + 17,
                            recovery: this.RECOVERY[ecLevel],
                            ecLevel
                        };
                    }
                }
            }
        }

        // Data too big for max QR version 40 with any EC level
        return null;
    }

    async mint(
        isBlack: boolean,
        data: string,
        ecLevel: EcLevel | null = null
    ): Promise<string> {
        const props = this.selectBestQrVersion2(data, ecLevel);
        const version = props?.version || undefined;
        const errorCorrectionLevel = props?.ecLevel || undefined;

        return new Promise((resolve, reject) => {
            QRCode.toDataURL(
                data,
                {
                    width: 1000, // resolution
                    errorCorrectionLevel, // undefined = M
                    type: "image/png",
                    margin: 0,
                    color: {
                        // requires high contrast to detect
                        // white against silver heart is dificult to detect
                        // 000 conflicts with gif transparency
                        // 555 works for import
                        // 444 works for paste
                        dark: isBlack ? "#111" : "#003366", // dark navy blue
                        light: "#0000" // transparent background
                    },
                    version // undefined = smallest version
                },
                (error: Error | null | undefined, url: string) => {
                    if (error) {
                        reject(
                            new Error(
                                `Error generating QR Code: ${error.message}`
                            )
                        );
                    } else {
                        resolve(url);
                    }
                }
            );
        });
    }

    async mineFile(file: File): Promise<string | null> {
        // Create a URL for the file
        const imageUrl = URL.createObjectURL(file);
        const image = new Image();

        try {
            // Wait for the image to load
            await new Promise<void>((resolve, reject) => {
                image.onload = () => resolve();
                image.onerror = (err) =>
                    reject(new Error(`Failed to load image: ${err}`));
                image.src = imageUrl;
            });

            // Create a canvas to draw the image onto
            const canvas = document.createElement("canvas");
            canvas.width = image.width;
            canvas.height = image.height;
            const context = canvas.getContext("2d", {
                willReadFrequently: true
            });

            if (!context) {
                console.error(
                    "Could not get 2D rendering context from canvas."
                );
                return null;
            }

            context.drawImage(image, 0, 0, image.width, image.height);
            return await this.mine(canvas);

            // // Get the image data from the canvas
            // const imageData = context.getImageData(
            //     0,
            //     0,
            //     canvas.width,
            //     canvas.height
            // );

            // // Use jsQR to scan the image data
            // const code = jsQR(
            //     imageData.data,
            //     imageData.width,
            //     imageData.height,
            //     {
            //         inversionAttempts: "attemptBoth" // Try both normal and inverted
            //     }
            // );

            // return code ? code.data : null;
        } finally {
            // Clean up the object URL to avoid memory leaks
            URL.revokeObjectURL(imageUrl);
        }
    }

    // cannot access system file! will just get file path
    async mineClipboard(): Promise<{ data: string; blob: Blob } | null> {
        try {
            // 1. Request permission and read clipboard contents
            const clipboardItems = await navigator.clipboard.read();

            // 2. Find the first image item on the clipboard
            for (const item of clipboardItems) {
                //console.log(item);
                // const imageType = item.types.find((type) =>
                //     type.startsWith("image/")
                // );
                for (const type of item.types) {
                    if (type.startsWith("image/")) {
                        const blob = await item.getType(type);

                        const canvas = await this.getCanvasFromBlob(blob);
                        if (!canvas) continue;

                        const code = await this.mine(canvas);
                        if (code) {
                            //console.log("Found QR code: ", code);
                            return { data: code, blob }; // Success! Return the data.
                        }

                        // // 3. Convert Blob to ImageData for jsqr
                        // const imageData = await this.getImageDataFromBlob(blob);
                        // if (!imageData) continue; // Could not process this blob, try next item

                        // // 4. Decode the QR code using jsQR
                        // const code = jsQR(
                        //     imageData.data,
                        //     imageData.width,
                        //     imageData.height,
                        //     {
                        //         inversionAttempts: "attemptBoth" // Try both normal and inverted
                        //     }
                        // );

                        // if (code) {
                        //     console.log("Found QR code:", code.data);
                        //     return { data: code.data, blob }; // Success! Return the data.
                        // }
                    } else if (type.startsWith("text/")) {
                        const blob = await item.getType(type);
                        const html = await blob.text();
                        console.log(html);

                        const canvas = await this.getCanvasFromHtml(html);
                        if (!canvas) continue;

                        const code = await this.mine(canvas);
                        if (code) {
                            //console.log("Found QR code: ", code);
                            return { data: code, blob }; // Success! Return the data.
                        }
                    }
                }
            }

            // If we get here, no scannable QR code was found in any image on the clipboard
            console.log("No QR code found on the clipboard.");
            return null;
        } catch (err) {
            // Handle potential errors, like permission denial
            console.error("Failed to read from clipboard: ", err);
            //if (err instanceof DOMException && err.name === "NotAllowedError") {
            // throw new Error(
            //     "Clipboard permission was denied. Please allow access in your browser settings and try again."
            // );
            //}
            return null;
        }
    }

    private async mine(canvas: HTMLCanvasElement): Promise<string | null> {
        // attempt 1:
        console.info("First Attempt...");

        const context = canvas.getContext("2d", {
            willReadFrequently: true
        });

        if (!context) {
            console.error("Could not get 2D rendering context from canvas.");
            return null;
        }
        this.enhanceContrast(context, canvas.width, canvas.height);

        // Get the image data from the canvas
        const imageData = context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
        );

        const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "attemptBoth" // Try both normal and inverted
        });

        if (code) return code.data;

        // atttempt 2:
        console.info("Second Attempt...");

        try {
            const result = await this.reader.decodeFromCanvas(canvas);
            return result.getText();
        } catch (error) {
            // attempt 3:
            console.info("Third Attempt...");
            //console.warn("Second attempt failed: ", error);

            // Configure options for QR decoding (you can customize these)
            const options: ReaderOptions = {
                tryHarder: true, // Try harder to decode (slower but more accurate)
                formats: ["QRCode"], // Limit to QR codes only
                maxNumberOfSymbols: 1 // Decode up to 1 symbol (adjust if expecting multiples)
            };

            try {
                const results: ReadResult[] = await readBarcodes(
                    imageData,
                    options
                );

                if (results.length > 0 && results[0]) {
                    // Return the text content of the first decoded QR code.
                    return results[0].text;
                } else {
                    // No QR code was found in the canvas.
                    console.warn("No QR code found");
                    return null;
                }
            } catch (error) {
                console.error("all attempts failed: ", error);
                return null;
            }
        }
    }

    // helpers

    // ios doesn't allow copying and pasting
    private async getCanvasFromHtml(
        html: string
    ): Promise<HTMLCanvasElement | null> {
        try {
            // Parse the HTML string into a DOM document
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");

            // Find all <img> elements
            const imgs = doc.querySelectorAll("img");
            if (imgs.length === 0) {
                console.warn("No images found in clipboard HTML");
                return null;
            }

            // Take the first image (adjust if multiple)
            const img = imgs[0] as HTMLImageElement;
            const src = img.src;

            // Check if it's a base64 data URI
            if (src.startsWith("data:image/")) {
                const blob = this.dataURItoBlob(src);
                return this.getCanvasFromBlob(blob);
            } else {
                console.warn("Image src is not a base64 data URI:", src);
                // If it's a file:// URL (rare in Notes copies), you might need to fetch it,
                // but cross-origin restrictions often block this in web contexts.
                return null;
            }
        } catch (error) {
            console.error("Failed to read clipboard:", error);
            return null;
        }
    }

    // Helper function to convert data URI to Blob
    private dataURItoBlob(dataURI: string): Blob {
        const [header, data] = dataURI.split(",");
        const mime = header.split(":")[1].split(";")[0];
        const byteString = atob(data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const intArray = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            intArray[i] = byteString.charCodeAt(i);
        }
        return new Blob([arrayBuffer], { type: mime });
    }

    private async getCanvasFromBlob(
        blob: Blob
    ): Promise<HTMLCanvasElement | null> {
        try {
            // Create an ImageBitmap from the Blob. This is more efficient than using an Image element.
            const imageBitmap = await createImageBitmap(blob);
            const { width, height } = imageBitmap;

            // Create an off-screen canvas
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            // Get the 2D rendering context
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                console.error("Could not create 2D rendering context.");
                return null;
            }

            // Draw the image onto the canvas
            ctx.drawImage(imageBitmap, 0, 0);

            return canvas;
        } catch (error) {
            console.error("Error converting Blob to ImageData:", error);
            return null;
        }
    }

    /**
     * Helper function to convert an image Blob to an ImageData object.
     */
    private async getImageDataFromBlob(blob: Blob): Promise<ImageData | null> {
        try {
            // Create an ImageBitmap from the Blob. This is more efficient than using an Image element.
            const imageBitmap = await createImageBitmap(blob);
            const { width, height } = imageBitmap;

            // Create an off-screen canvas
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            // Get the 2D rendering context
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                console.error("Could not create 2D rendering context.");
                return null;
            }

            // Draw the image onto the canvas
            ctx.drawImage(imageBitmap, 0, 0);

            // Get the ImageData from the canvas
            return ctx.getImageData(0, 0, width, height);
        } catch (error) {
            console.error("Error converting Blob to ImageData:", error);
            return null;
        }
    }

    private enhanceContrast(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number
    ) {
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            const gray =
                0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            const high = gray < 128 ? 0 : 255; // simple threshold
            data[i] = data[i + 1] = data[i + 2] = high;
        }
        ctx.putImageData(imageData, 0, 0);
    }
}
